import { assign, createMachine, DoneInvokeEvent, sendParent } from 'xstate';
import { ACTIONS, EVENTS, GUARDS, InitialState, SERVICES, STATES } from '../../interfaces/microphoneInterface';
import { EVENTS as RECORDER_EVENTS } from '../../interfaces/recorderInterface';
import { SentryBreadcrumb, SentryTrackingFn } from '../../utils/sentry.utils';
import { recorderErrors } from '../../utils/recorderErrors.utils';
import { RETAKE_SPEED } from '../../utils/constants.utils';
import WorkerBuilder from '../../workers/worker-builder';
import Worker from '../../workers/microphone.worker';
import { emitTrackEvent, EVENT_TYPES } from '../../services/event.service';

export const microphoneMachine = createMachine<InitialState, DoneInvokeEvent<any>>({
  predictableActionArguments: true,
  id: 'microphone',
  initial: STATES.INACTIVE,
  context: {
    retakeSpeed: RETAKE_SPEED.MEDIUM,
    mediaStream: null,
    micData: 0,
    retakes: 0, // TODO: George update this variable to something more understandble like 'score',
    webWorker: null,
  },
  on: {
    [EVENTS.ON_SET_MEDIA_STREAM]: {
      actions: [ACTIONS.SET_MEDIA_STREAM, ACTIONS.INIT_WEB_WORKER],
      target: STATES.INACTIVE,
    },
    [EVENTS.ON_DETECTION_SPEED]: {
      actions: [ACTIONS.SET_DETECTION_SPEED],
    },
  },
  states: {
    [STATES.INACTIVE]: {
      always: { target: STATES.ACTIVE, cond: GUARDS.IS_MEDIA_STREAM_AVAILABLE },
    },
    [STATES.ACTIVE]: {
      initial: STATES.DATA_CHECK,
      invoke: {
        id: 'checkMicrophone',
        src: SERVICES.CHECK_MICROPHONE_LEVEL,
      },
      states: {
        [STATES.DATA_CHECK]: {
          entry: [sendParent((context, event) => ({ type: RECORDER_EVENTS.MIC_DATA_RECEIVED }))],
          always: [
            { target: STATES.NOT_RECEIVING_DATA, cond: GUARDS.IS_MIC_NOT_RECEIVING_DATA },
          ],
          on: {
            [EVENTS.MUTE]: {
              target: [STATES.NOT_RECEIVING_DATA],
            },
          },
        },
        [STATES.NOT_RECEIVING_DATA]: {
          entry: [
            ACTIONS.COLLECT_BREADCRUMB,
            sendParent((context, event) => ({
              data: recorderErrors.NoSoundError,
              type: RECORDER_EVENTS.RECORDER_DEVICE_ERROR,
            })),
            { type: ACTIONS.SENTRY, data: { eventName: recorderErrors.NoSoundError.name, level: 'info' } },
            (context) => emitTrackEvent({
              eventType: EVENT_TYPES.NO_SOUND,
              extraData: {
                enabled: context?.mediaStream?.getAudioTracks()[0].enabled,
                label: context?.mediaStream?.getAudioTracks()[0].label,
                muted: context?.mediaStream?.getAudioTracks()[0].muted,
              } }),
          ],
          always: {
            target: STATES.DATA_CHECK,
            actions: [
              (context) => emitTrackEvent({
                eventType: EVENT_TYPES.SOUND_RESTORED,
                extraData: {
                  enabled: context?.mediaStream?.getAudioTracks()[0].enabled,
                  label: context?.mediaStream?.getAudioTracks()[0].label,
                  muted: context?.mediaStream?.getAudioTracks()[0].muted,
                } }),
            ],
            cond: GUARDS.IS_MIC_RECEIVING_DATA,
          },
          on: {
            [EVENTS.UNMUTE]: {
              target: [STATES.DATA_CHECK],
              actions: [
                (context) => emitTrackEvent({
                  eventType: EVENT_TYPES.SOUND_RESTORED,
                  extraData: {
                    enabled: context?.mediaStream?.getAudioTracks()[0].enabled,
                    label: context?.mediaStream?.getAudioTracks()[0].label,
                    muted: context?.mediaStream?.getAudioTracks()[0].muted,
                  } }),
              ],
            },
          },
        },
      },
      on: {
        [EVENTS.SEND_DATA]: {
          actions: [ACTIONS.UPDATE_MIC],
        },
        [EVENTS.CLEAN_RETAKES]: {
          actions: [ACTIONS.CLEAN_RETAKES],
        },
        [EVENTS.UPDATE_RETAKES]: {
          actions: [ACTIONS.UPDATE_RETAKES],
        },
      },
      exit: [assign({ mediaStream: null })],
    },
  },
}, {
  actions: {
    [ACTIONS.COLLECT_BREADCRUMB]: (context) => {
      SentryBreadcrumb({
        type: 'debug',
        level: 'warning',
        category: 'mic',
        message: 'mic is inactive, this is the status of audioTrack',
        data: {
          audioTrack: {
            enabled: context?.mediaStream?.getAudioTracks()[0].enabled,
            label: context?.mediaStream?.getAudioTracks()[0].label,
            muted: context?.mediaStream?.getAudioTracks()[0].muted,
          },
        },
      });
    },
    [ACTIONS.INIT_WEB_WORKER]: assign({
      webWorker: (context, _event) => (context.mediaStream ? new WorkerBuilder(Worker as unknown as any) : null),
    }),
    [ACTIONS.SENTRY]: (context, event, meta) => {
      SentryTrackingFn(event, context, meta.action.data?.eventName || 'microphoneMachine', meta.action.data?.level);
    },
    [ACTIONS.CLEAN_RETAKES]: assign({
      retakes: (context, event) => 0,
    }),
    [ACTIONS.UPDATE_RETAKES]: assign({
      retakes: (context, event) => Math.min(context.retakes + 1, context.retakeSpeed),
    }),
    [ACTIONS.CONSOLE_LOG]: (context, event) => console.log('Microphone Machine:', context, event),
    [ACTIONS.SET_MEDIA_STREAM]: assign((context, event) => ({
      mediaStream: event.data,
      retakes: 0,
      micData: 0,
    })),
    [ACTIONS.UPDATE_MIC]: assign({
      micData: (context, event) => event.data,
    }),
    [ACTIONS.SET_DETECTION_SPEED]: assign({
      retakeSpeed: (_context, event) => event.data,
    }),
  },
  services: {
    [SERVICES.CHECK_MICROPHONE_LEVEL]: (context, event) => (callback, onReceive) => {
      const { mediaStream } = context;
      if (mediaStream) {
        mediaStream.getAudioTracks()[0].onmute = (eventSound) => {
          callback('MUTE');
        };
        mediaStream.getAudioTracks()[0].onunmute = (eventSound) => {
          callback('UNMUTE');
        };
      }

      const ctx = new AudioContext();
      let mic: MediaStreamAudioSourceNode | null = ctx.createMediaStreamSource(context.mediaStream!);
      let analyser: AnalyserNode | null = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.minDecibels = -100;
      analyser.maxDecibels = 0;
      mic.connect(analyser);
      emitTrackEvent({ eventType: 'micInitialized', extraData: { deviceName: mediaStream?.getAudioTracks()[0].label } });
      const data = new Uint8Array(analyser.frequencyBinCount);
      let localMicData = 0;
      // worker
      function getVolume(percentage:number) {
        if (context.webWorker) {
          analyser?.getByteFrequencyData(data);
          context.webWorker.postMessage(data);
        }
        if (localMicData === percentage) {
          callback(EVENTS.UPDATE_RETAKES);
        } else {
          callback(EVENTS.CLEAN_RETAKES);
          localMicData = percentage;
          callback({ type: EVENTS.SEND_DATA, data: percentage });
        }
      }
      if (context.webWorker) {
        context.webWorker.onmessage = (message) => {
          requestAnimationFrame(() => getVolume(message.data));
        };
      }
      getVolume(0);
      ctx.onstatechange = () => {
        if (ctx.state === 'suspended' || ctx.state === 'interrupted' as string) {
          SentryTrackingFn(
            { type: 'status === suspended', data: ctx.state },
            { label: context?.mediaStream?.getAudioTracks()[0].label,
              muted: context?.mediaStream?.getAudioTracks()[0].muted,
              status: ctx.state },
            'mic onstatechange',
            'fatal',
          );
        }
      };
      return () => {
        mic = null;
        analyser = null;
        ctx.close();
        localMicData = 0;
        context.webWorker?.terminate();
      };
    },
  },
  guards: {
    [GUARDS.IS_MIC_RECEIVING_DATA]: ({ retakeSpeed, retakes, mediaStream }) => (retakes !== retakeSpeed && !mediaStream?.getAudioTracks()[0].muted),
    [GUARDS.IS_MIC_NOT_RECEIVING_DATA]: ({ retakeSpeed, retakes }) => retakes === retakeSpeed,
    [GUARDS.IS_MEDIA_STREAM_AVAILABLE]: ({ mediaStream }) => !!mediaStream,
  },
});
